import React, { useContext, useState, useMemo, useEffect, useCallback } from 'react';
import {
  Card,
  CardBody,
  Col,
  Container,
  CardHeader,
  Nav,
  NavItem,
  NavLink,
  Row,
  Modal,
  ModalHeader,
  Form,
  ModalBody,
  Label,
  Input,
  FormFeedback,
  Button,
} from 'reactstrap';
import moment from 'moment';
import 'moment/locale/fa';
import { Link } from 'react-router-dom';
import classnames from 'classnames';
import BreadCrumb from '../Components/Common/BreadCrumb';
import TableContainer from '../Components/Common/TableContainer';
import DeleteModal from '../Components/Common/DeleteModal';
import { CommentContext } from '../context/CommentContext';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { useFormik } from 'formik';
import * as Yup from 'yup';

// نگاشت وضعیت به فارسی
const mapStatus = (status) => {
  switch (status) {
    case 0:
      return 'در انتظار';
    case 1:
      return 'تأییدشده';
    case 2:
      return 'ردشده';
    default:
      return 'نامشخص';
  }
};

const Comments = () => {
  document.title = 'نظرات بلاگ | فات 90';

  const { comments, loading, error, fetchComments, updateCommentStatus, addReply, removeComment } =
    useContext(CommentContext);
  const [comment, setComment] = useState(null);
  const [modal, setModal] = useState(false);
  const [viewModal, setViewModal] = useState(false);
  const [selectedComment, setSelectedComment] = useState(null);
  const [activeTab, setActiveTab] = useState('1');
  const [deleteModal, setDeleteModal] = useState(false);
  const [deleteModalMulti, setDeleteModalMulti] = useState(false);
  const [selectedCheckBoxDelete, setSelectedCheckBoxDelete] = useState([]);
  const [isMultiDeleteButton, setIsMultiDeleteButton] = useState(false);
  const [commentList, setCommentList] = useState([]);

  // تعداد نظرات برای هر تب
  const commentCounts = useMemo(() => ({
    all: comments.length,
    pending: comments.filter((c) => c.status === 0).length,
    approved: comments.filter((c) => c.status === 1).length,
    rejected: comments.filter((c) => c.status === 2).length,
  }), [comments]);

  // اعتبارسنجی فرم پاسخ
  const validation = useFormik({
    enableReinitialize: true,
    initialValues: {
      reply: comment?.adminReply || '',
    },
    validationSchema: Yup.object({
      reply: Yup.string()
        .required('لطفاً پاسخ را وارد کنید')
        .max(500, 'پاسخ باید حداکثر 500 کاراکتر باشد'),
    }),
    onSubmit: async (values) => {
      try {
        await addReply(comment.id, values.reply);
        toggle();
        validation.resetForm();
      } catch (err) {
        // خطاها توسط CommentContext مدیریت می‌شن
      }
    },
  });

  // مدیریت مودال پاسخ
  const toggle = useCallback(() => {
    if (modal) {
      setModal(false);
      setComment(null);
      validation.resetForm();
    } else {
      setModal(true);
    }
  }, [modal, validation]);

  // مدیریت مودال نمایش
  const toggleViewModal = useCallback(() => {
    setViewModal(!viewModal);
    if (viewModal) {
      setSelectedComment(null);
    }
  }, [viewModal]);

  // پاسخ به نظر
  const handleReplyClick = useCallback(
    (commentData) => {
      setComment({
        id: commentData.id,
        senderName: commentData.senderName,
        message: commentData.message || '',
        adminReply: commentData.adminReply || '',
      });
      validation.setFieldValue('reply', commentData.adminReply || '');
      toggle();
    },
    [toggle, validation]
  );

  // نمایش اطلاعات نظر
  const handleViewClick = useCallback(
    (commentData) => {
      setSelectedComment(commentData);
      toggleViewModal();
    },
    [toggleViewModal]
  );

  // تغییر وضعیت نظر
  const handleStatusChange = async (id, status) => {
    try {
      await updateCommentStatus(id, status);
    } catch (err) {
      // خطاها توسط CommentContext مدیریت می‌شن
    }
  };

  // حذف نظر
  const onClickDelete = (commentData) => {
    setComment(commentData);
    setDeleteModal(true);
  };

  const handleDeleteComment = async () => {
    if (comment) {
      try {
        await removeComment(comment.id);
        setDeleteModal(false);
        setComment(null);
      } catch (err) {
        // خطاها توسط CommentContext مدیریت می‌شن
      }
    }
  };

  // حذف گروهی
  const checkedAll = useCallback(() => {
    const checkall = document.getElementById('checkBoxAll');
    const ele = document.querySelectorAll('.commentCheckBox');
    if (checkall.checked) {
      ele.forEach((el) => {
        el.checked = true;
      });
    } else {
      ele.forEach((el) => {
        el.checked = false;
      });
    }
    deleteCheckbox();
  }, []);

  const deleteCheckbox = () => {
    const ele = document.querySelectorAll('.commentCheckBox:checked');
    setSelectedCheckBoxDelete(ele);
    setIsMultiDeleteButton(ele.length > 0);
  };

  const deleteMultiple = async () => {
    const checkall = document.getElementById('checkBoxAll');
    try {
      for (const element of selectedCheckBoxDelete) {
        await removeComment(parseInt(element.value));
      }
      setIsMultiDeleteButton(false);
      checkall.checked = false;
      setDeleteModalMulti(false);
    } catch (err) {
      // خطاها توسط CommentContext مدیریت می‌شن
    }
  };

  // فیلتر بر اساس وضعیت
  const toggleTab = (tab, status) => {
    if (activeTab !== tab) {
      setActiveTab(tab);
      fetchComments(status === 'all' ? null : parseInt(status));
    }
  };

  // دریافت نظرات در بارگذاری اولیه
  useEffect(() => {
    fetchComments();
  }, [fetchComments]);

  // به‌روزرسانی لیست نظرات بر اساس تب فعال
  useEffect(() => {
    console.log('Comments:', comments); // لاگ برای دیباگ
    const mappedComments = comments
      .filter((comment) => {
        if (!comment.message || !comment.postTitle) {
          console.warn('Invalid comment:', comment); // لاگ ردیف‌های ناقص
          return false;
        }
        return true;
      })
      .map((comment) => ({
        ...comment,
        statusDisplay: mapStatus(comment.status),
      }));
    setCommentList(
      activeTab === '1'
        ? mappedComments
        : mappedComments.filter((comment) => {
          if (activeTab === '2') return comment.status === 0;
          if (activeTab === '3') return comment.status === 1;
          if (activeTab === '4') return comment.status === 2;
          return true;
        })
    );
  }, [comments, activeTab]);

  // ستون‌های جدول
  const columns = useMemo(
    () => [
      {
        header: (
          <input
            type="checkbox"
            id="checkBoxAll"
            className="form-check-input"
            onChange={checkedAll}
          />
        ),
        cell: (cell) => (
          <input
            type="checkbox"
            className="commentCheckBox form-check-input"
            value={cell.getValue()}
            onChange={deleteCheckbox}
          />
        ),
        id: '#',
        accessorKey: 'id',
        enableColumnFilter: false,
        enableSorting: false,
      },
      {
        header: 'نام فرستنده',
        accessorKey: 'senderName',
        enableColumnFilter: false,
        cell: (cell) => cell.getValue() || 'بدون نام',
      },
      {
        header: 'پیام',
        accessorKey: 'message',
        enableColumnFilter: false,
        cell: (cell) => {
          const message = cell.getValue() || '';
          return (
            <Link
              to="#"
              className="fw-medium link-primary"
              onClick={() => handleViewClick(cell.row.original)}
              title={message}
            >
              {message.length > 50 ? message.substring(0, 50) + '...' : message}
            </Link>
          );
        },
      },
      {
        header: 'عنوان پست',
        accessorKey: 'postTitle',
        enableColumnFilter: false,
        cell: (cell) => {
          const postTitle = cell.getValue() || 'بدون عنوان';
          return (
            <a
              href={`${process.env.REACT_APP_BLOG_BASE_URL}${process.env.REACT_APP_BLOG_PATH}/${cell.row.original.postSlug}`}
              target="_blank"
              rel="noopener noreferrer"
              className="fw-medium link-primary"
              title={postTitle}
            >
              {postTitle.length > 30 ? postTitle.substring(0, 30) + '...' : postTitle}
            </a>
          );
        },
      },
      {
        header: 'وضعیت',
        accessorKey: 'statusDisplay',
        enableColumnFilter: false,
        cell: (cell) => {
          const status = cell.row.original.status;
          switch (status) {
            case 0:
              return <span className="badge bg-warning-subtle text-warning text-uppercase">در انتظار</span>;
            case 1:
              return <span className="badge bg-success-subtle text-success text-uppercase">تأییدشده</span>;
            case 2:
              return <span className="badge bg-danger-subtle text-danger text-uppercase">ردشده</span>;
            default:
              return <span className="badge bg-secondary-subtle text-secondary text-uppercase">نامشخص</span>;
          }
        },
      },
      {
        header: 'تاریخ ارسال',
        accessorKey: 'createdAt',
        enableColumnFilter: false,
        cell: (cell) => moment(cell.getValue()).locale('fa').format('DD MMMM YYYY, HH:mm'),
      },
      {
        header: 'پاسخ ادمین',
        accessorKey: 'adminReply',
        enableColumnFilter: false,
        cell: (cell) => {
          const reply = cell.getValue();
          return (
            <span title={reply}>
              {reply ? (reply.length > 30 ? reply.substring(0, 30) + '...' : reply) : '-'}
            </span>
          );
        },
      },
      {
        header: 'تاریخ پاسخ',
        accessorKey: 'repliedAt',
        enableColumnFilter: false,
        cell: (cell) =>
          cell.getValue() ? moment(cell.getValue()).locale('fa').format('DD MMMM YYYY, HH:mm') : '-',
      },
      {
        header: 'عملیات',
        cell: (cellProps) => {
          const status = cellProps.row.original.status;
          return (
            <ul className="list-inline hstack gap-2 mb-0">
              {status === 0 && (
                <>
                  <li className="list-inline-item">
                    <Button
                      size="sm"
                      color="soft-success"
                      onClick={() => handleStatusChange(cellProps.row.original.id, 1)}
                    >
                      تأیید
                    </Button>
                  </li>
                  <li className="list-inline-item">
                    <Button
                      size="sm"
                      color="soft-danger"
                      onClick={() => handleStatusChange(cellProps.row.original.id, 2)}
                    >
                      رد
                    </Button>
                  </li>
                </>
              )}
              {status === 1 && (
                <li className="list-inline-item">
                  <Button
                    size="sm"
                    color="soft-primary"
                    onClick={() => handleReplyClick(cellProps.row.original)}
                  >
                    پاسخ
                  </Button>
                </li>
              )}
              <li className="list-inline-item">
                <Link
                  to="#"
                  className="text-danger d-inline-block remove-item-btn"
                  onClick={() => onClickDelete(cellProps.row.original)}
                >
                  <i className="ri-delete-bin-5-fill fs-16"></i>
                </Link>
              </li>
            </ul>
          );
        },
      },
    ],
    [checkedAll, handleReplyClick, handleViewClick]
  );

  return (
    <div className="page-content" dir="rtl">
      <ToastContainer closeButton={false} limit={1} />
      <DeleteModal
        show={deleteModal}
        onDeleteClick={handleDeleteComment}
        onCloseClick={() => setDeleteModal(false)}
      />
      <DeleteModal
        show={deleteModalMulti}
        onDeleteClick={() => {
          deleteMultiple();
          setDeleteModalMulti(false);
        }}
        onCloseClick={() => setDeleteModalMulti(false)}
      />
      <Container fluid>
        <BreadCrumb title="نظرات بلاگ" pageTitle="مدیریت" />
        <Row>
          <Col lg={12}>
            <Card id="commentList">
              <CardHeader className="card-header border-0">
                <Row className="align-items-center gy-3">
                  <div className="col-sm">
                    <h5 className="card-title mb-0">لیست نظرات</h5>
                  </div>
                  <div className="col-sm-auto">
                    <div className="d-flex gap-1 flex-wrap">
                      {isMultiDeleteButton && (
                        <Button
                          color="soft-danger"
                          onClick={() => setDeleteModalMulti(true)}
                        >
                          <i className="ri-delete-bin-2-line"></i>
                        </Button>
                      )}
                    </div>
                  </div>
                </Row>
              </CardHeader>
              <CardBody className="pt-0">
                <div>
                  <Nav className="nav-tabs nav-tabs-custom nav-primary" role="tablist">
                    <NavItem>
                      <NavLink
                        className={classnames({ active: activeTab === '1' })}
                        onClick={() => toggleTab('1', 'all')}
                        href="#"
                      >
                        <i className="ri-store-2-fill me-1 align-bottom"></i> همه نظرات ({commentCounts.all.toLocaleString('fa-IR')})
                      </NavLink>
                    </NavItem>
                    <NavItem>
                      <NavLink
                        className={classnames({ active: activeTab === '2' })}
                        onClick={() => toggleTab('2', '0')}
                        href="#"
                      >
                        <i className="ri-time-line me-1 align-bottom"></i> در انتظار ({commentCounts.pending.toLocaleString('fa-IR')})
                      </NavLink>
                    </NavItem>
                    <NavItem>
                      <NavLink
                        className={classnames({ active: activeTab === '3' })}
                        onClick={() => toggleTab('3', '1')}
                        href="#"
                      >
                        <i className="ri-checkbox-circle-line me-1 align-bottom"></i> تأییدشده ({commentCounts.approved.toLocaleString('fa-IR')})
                      </NavLink>
                    </NavItem>
                    <NavItem>
                      <NavLink
                        className={classnames({ active: activeTab === '4' })}
                        onClick={() => toggleTab('4', '2')}
                        href="#"
                      >
                        <i className="ri-close-circle-line me-1 align-bottom"></i> ردشده ({commentCounts.rejected.toLocaleString('fa-IR')})
                      </NavLink>
                    </NavItem>
                  </Nav>
                  {loading ? (
                    <div className="py-4 text-center">
                      <lord-icon
                        src="https://cdn.lordicon.com/msoeawqm.json"
                        trigger="loop"
                        colors="primary:#25a0e2,secondary:#00bd9d"
                        style={{ width: '72px', height: '72px' }}
                      ></lord-icon>
                      <h5 className="mt-4">در حال بارگذاری...</h5>
                    </div>
                  ) : error ? (
                    <div className="py-4 text-center">
                      <lord-icon
                        src="https://cdn.lordicon.com/msoeawqm.json"
                        trigger="loop"
                        colors="primary:#25a0e2,secondary:#00bd9d"
                        style={{ width: '72px', height: '72px' }}
                      ></lord-icon>
                      <h5 className="mt-4">{error}</h5>
                    </div>
                  ) : commentList.length ? (
                    <TableContainer
                      columns={columns}
                      data={commentList || []}
                      isGlobalFilter={true}
                      isAddUserList={false}
                      customPageSize={8}
                      divClass="table-responsive table-card mb-1"
                      tableClass="align-middle table-nowrap table-hover"
                      theadClass="table-light text-muted"
                      isOrderFilter={true}
                      SearchPlaceholder="جستجو بر اساس نام فرستنده، پیام یا عنوان پست..."
                    />
                  ) : (
                    <div className="py-4 text-center">
                      <lord-icon
                        src="https://cdn.lordicon.com/msoeawqm.json"
                        trigger="loop"
                        colors="primary:#25a0e2,secondary:#00bd9d"
                        style={{ width: '72px', height: '72px' }}
                      ></lord-icon>
                      <h5 className="mt-4">هیچ نظری یافت نشد</h5>
                    </div>
                  )}
                </div>
                <Modal id="replyModal" isOpen={modal} toggle={toggle} centered size="lg">
                  <ModalHeader className="bg-light p-3" toggle={toggle}>
                    پاسخ به نظر
                  </ModalHeader>
                  <Form
                    className="tablelist-form"
                    onSubmit={(e) => {
                      e.preventDefault();
                      validation.handleSubmit();
                      return false;
                    }}
                  >
                    <ModalBody>
                      <Row>
                        <Col md={12}>
                          <div className="mb-3">
                            <Label className="form-label">نام فرستنده</Label>
                            <Input
                              type="text"
                              className="form-control"
                              value={comment?.senderName || 'بدون نام'}
                              disabled
                            />
                          </div>
                        </Col>
                        <Col md={12}>
                          <div className="mb-3">
                            <Label className="form-label">پیام</Label>
                            <Input
                              type="textarea"
                              className="form-control"
                              rows="3"
                              value={comment?.message || ''}
                              disabled
                            />
                          </div>
                        </Col>
                        <Col md={12}>
                          <div className="mb-3">
                            <Label htmlFor="reply-field" className="form-label">
                              پاسخ ادمین
                            </Label>
                            <Input
                              name="reply"
                              id="reply-field"
                              className="form-control"
                              type="textarea"
                              rows="5"
                              placeholder="پاسخ خود را وارد کنید"
                              onChange={validation.handleChange}
                              onBlur={validation.handleBlur}
                              value={validation.values.reply || ''}
                              invalid={validation.touched.reply && !!validation.errors.reply}
                            />
                            <FormFeedback>{validation.errors.reply}</FormFeedback>
                          </div>
                        </Col>
                      </Row>
                    </ModalBody>
                    <div className="modal-footer">
                      <div className="hstack gap-2 justify-content-end">
                        <Button type="button" className="btn btn-light" onClick={toggle}>
                          بستن
                        </Button>
                        <Button type="submit" className="btn btn-success" disabled={loading}>
                          ثبت پاسخ
                        </Button>
                      </div>
                    </div>
                  </Form>
                </Modal>
                <Modal id="viewCommentModal" isOpen={viewModal} toggle={toggleViewModal} centered size="lg">
                  <ModalHeader className="bg-light p-3" toggle={toggleViewModal}>
                    جزئیات نظر
                  </ModalHeader>
                  <ModalBody>
                    <Row>
                      <Col md={12}>
                        <div className="mb-3">
                          <Label className="form-label">نام فرستنده</Label>
                          <Input
                            type="text"
                            className="form-control"
                            value={selectedComment?.senderName || 'بدون نام'}
                            disabled
                          />
                        </div>
                      </Col>
                      <Col md={12}>
                        <div className="mb-3">
                          <Label className="form-label">پیام</Label>
                          <Input
                            type="textarea"
                            className="form-control"
                            rows="5"
                            value={selectedComment?.message || ''}
                            disabled
                          />
                        </div>
                      </Col>
                      <Col md={12}>
                        <div className="mb-3">
                          <Label className="form-label">پاسخ ادمین</Label>
                          <Input
                            type="textarea"
                            className="form-control"
                            rows="5"
                            value={selectedComment?.adminReply || 'بدون پاسخ'}
                            disabled
                          />
                        </div>
                      </Col>
                      <Col md={6}>
                        <div className="mb-3">
                          <Label className="form-label">تاریخ ارسال</Label>
                          <Input
                            type="text"
                            className="form-control"
                            value={
                              selectedComment?.createdAt
                                ? moment(selectedComment.createdAt).locale('fa').format('DD MMMM YYYY, HH:mm')
                                : '-'
                            }
                            disabled
                          />
                        </div>
                      </Col>
                      <Col md={6}>
                        <div className="mb-3">
                          <Label className="form-label">تاریخ پاسخ</Label>
                          <Input
                            type="text"
                            className="form-control"
                            value={
                              selectedComment?.repliedAt
                                ? moment(selectedComment.repliedAt).locale('fa').format('DD MMMM YYYY, HH:mm')
                                : 'بدون پاسخ'
                            }
                            disabled
                          />
                        </div>
                      </Col>
                    </Row>
                  </ModalBody>
                  <div className="modal-footer">
                    <Button type="button" className="btn btn-light" onClick={toggleViewModal}>
                      بستن
                    </Button>
                  </div>
                </Modal>
              </CardBody>
            </Card>
          </Col>
        </Row>
      </Container>
    </div>
  );
};

export default Comments;